import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquare, KeyRound, Smartphone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { twoFactorFailure } from '@/lib/twoFactor/failure'
import type { TwoFactorChannel, TwoFactorCodeSent } from '@/lib/sdk-official/resources/two-factor'
import { cn } from '@/lib/utils'

/** Where the last code went (`sentVia: null` when it could not go out, with `notice` saying why). */
export interface CodeDelivery {
  phone: string | null
  sentVia: TwoFactorChannel | null
  resendAfterSeconds?: number | null
}

interface TwoFactorCodeFormProps {
  delivery: CodeDelivery | null
  /** Why the first code did not go out (the login's `codeError`), shown before anything is typed. */
  notice?: { text: string; suggestRecovery: boolean } | null
  /** Throws the API's error; this form explains it. */
  onSubmit: (answer: { code?: string; recoveryCode?: string }) => Promise<void>
  onResend: (channel: TwoFactorChannel) => Promise<TwoFactorCodeSent>
  /** Whether a recovery code is accepted here (login and step-up: yes). */
  allowRecovery?: boolean
  submitLabel: string
  /** Below the field (the login's "Lembrar este dispositivo"). */
  extra?: React.ReactNode
  /** Next to the submit button (Cancel, Back). */
  secondary?: React.ReactNode
  testId?: string
}

/**
 * F-233: the code field shared by the login's second step, the step-up modal
 * and the setup: the 6 digits from WhatsApp, "Receber por SMS", and "Usar
 * código de recuperação" (one of the 10, single use).
 */
export const TwoFactorCodeForm = ({ delivery, notice, onSubmit, onResend, allowRecovery = true, submitLabel, extra, secondary, testId }: TwoFactorCodeFormProps) => {
  const { t } = useTranslation('twoFactor')
  const [mode, setMode] = React.useState<'code' | 'recovery'>('code')
  const [value, setValue] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [resending, setResending] = React.useState<TwoFactorChannel | null>(null)
  const [error, setError] = React.useState<{ text: string; suggestRecovery: boolean } | null>(notice ?? null)
  const [info, setInfo] = React.useState<string | null>(null)
  const [current, setCurrent] = React.useState<CodeDelivery | null>(delivery)
  const [waitUntil, setWaitUntil] = React.useState(() => Date.now() + (delivery?.resendAfterSeconds ?? 0) * 1000)
  const [now, setNow] = React.useState(Date.now())
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => { setCurrent(delivery) }, [delivery])
  React.useEffect(() => { setError(notice ?? null) }, [notice])
  React.useEffect(() => {
    if (waitUntil <= Date.now()) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [waitUntil])
  const waitSeconds = Math.max(0, Math.ceil((waitUntil - now) / 1000))

  const switchMode = (next: 'code' | 'recovery') => {
    setMode(next)
    setValue('')
    setError(null)
    setInfo(null)
    window.setTimeout(() => inputRef.current?.focus(), 0)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const typed = value.trim()
    if (!typed) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await onSubmit(mode === 'code' ? { code: typed.replace(/\D/g, '') } : { recoveryCode: typed })
    } catch (err) {
      const failure = twoFactorFailure(err, t)
      setError(failure)
      if (failure.param === 'code') setValue('')
    } finally {
      setBusy(false)
    }
  }

  const resend = async (channel: TwoFactorChannel) => {
    setResending(channel)
    setError(null)
    setInfo(null)
    try {
      const sent = await onResend(channel)
      setCurrent({ phone: sent.phone, sentVia: sent.sentVia, resendAfterSeconds: sent.resendAfterSeconds })
      setWaitUntil(Date.now() + (sent.resendAfterSeconds || 60) * 1000)
      setNow(Date.now())
      setInfo(sent.sentVia === 'sms' ? t('code.sentSms', { phone: sent.phone }) : t('code.sentWhatsapp', { phone: sent.phone }))
      if (mode !== 'code') switchMode('code')
    } catch (err) {
      setError(twoFactorFailure(err, t))
    } finally {
      setResending(null)
    }
  }

  const sentLine = current?.sentVia
    ? (current.sentVia === 'sms' ? t('code.sentSms', { phone: current.phone }) : t('code.sentWhatsapp', { phone: current.phone }))
    : null

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4" data-testid={testId} noValidate>
      {mode === 'code' ? (
        sentLine && !info && <p className="flex items-start gap-2 text-sm text-foreground-secondary"><MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />{sentLine}</p>
      ) : (
        <p className="text-sm text-foreground-secondary">{t('code.recoveryHelp')}</p>
      )}
      <div className="space-y-1.5">
        <label htmlFor="two-factor-code" className="block text-sm font-medium text-foreground">
          {mode === 'code' ? t('code.label') : t('code.recoveryLabel')}
        </label>
        <Input
          ref={inputRef}
          id="two-factor-code"
          value={value}
          onChange={(e) => setValue(mode === 'code' ? e.target.value.replace(/\D/g, '').slice(0, 8) : e.target.value.slice(0, 20))}
          inputMode={mode === 'code' ? 'numeric' : 'text'}
          autoComplete={mode === 'code' ? 'one-time-code' : 'off'}
          autoCapitalize="none"
          spellCheck={false}
          placeholder={mode === 'code' ? '000000' : 'xxxxx-xxxxx'}
          className={cn('h-12 text-center text-lg tracking-[0.3em]', mode === 'recovery' && 'tracking-widest')}
          aria-invalid={Boolean(error)}
          autoFocus
          disabled={busy}
          data-testid="two-factor-code-input"
        />
      </div>
      {extra}
      {info && <p role="status" className="rounded-lg bg-success-surface p-3 text-sm text-success-text">{info}</p>}
      {error && (
        <div role="alert" className="rounded-lg bg-danger-surface p-3 text-sm text-danger-text" data-testid="two-factor-code-error">
          <p>{error.text}</p>
          {error.suggestRecovery && allowRecovery && mode === 'code' && (
            <button type="button" className="mt-1 font-medium underline" onClick={() => switchMode('recovery')}>{t('code.tryRecovery')}</button>
          )}
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {secondary}
        <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={busy || !value.trim()} data-testid="two-factor-code-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
      <div className="flex flex-col items-start gap-2 border-t border-border pt-3 text-sm">
        {mode === 'code' ? (
          <>
            <button type="button" className="inline-flex items-center gap-2 font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
              onClick={() => void resend('sms')} disabled={Boolean(resending) || waitSeconds > 0} data-testid="two-factor-resend-sms">
              {resending === 'sms' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" aria-hidden="true" />}
              {waitSeconds > 0 ? t('code.smsIn', { seconds: waitSeconds }) : t('code.sendSms')}
            </button>
            {current?.sentVia !== 'whatsapp' && (
              <button type="button" className="inline-flex items-center gap-2 font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
                onClick={() => void resend('whatsapp')} disabled={Boolean(resending) || waitSeconds > 0} data-testid="two-factor-resend-whatsapp">
                {resending === 'whatsapp' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" aria-hidden="true" />}
                {t('code.sendWhatsapp')}
              </button>
            )}
            {allowRecovery && (
              <button type="button" className="inline-flex items-center gap-2 font-medium text-foreground-secondary hover:underline" onClick={() => switchMode('recovery')} data-testid="two-factor-use-recovery">
                <KeyRound className="h-4 w-4" aria-hidden="true" />{t('code.useRecovery')}
              </button>
            )}
          </>
        ) : (
          <button type="button" className="inline-flex items-center gap-2 font-medium text-primary hover:underline" onClick={() => switchMode('code')}>
            <MessageSquare className="h-4 w-4" aria-hidden="true" />{t('code.useCode')}
          </button>
        )}
      </div>
    </form>
  )
}
